import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import ServiceEvidenceUploader from './ServiceEvidenceUploader';
import ServiceEvidenceGallery from './ServiceEvidenceGallery';
import ServiceEvidenceAddendum from './ServiceEvidenceAddendum';
import { useAuth } from '../../contexts/AuthContext';

const blankReport = { diagnosis: '', workPerformed: '', testsPerformed: '', equipment: '', recommendations: '' };
const fields = [
  ['diagnosis', 'Findings / diagnosis', true, 'What did you find? Include the verified root cause, if known.'],
  ['workPerformed', 'Work performed', true, 'Document changes made, equipment configured, and steps taken.'],
  ['testsPerformed', 'Testing / verification', true, 'State how service was tested and whether the result passed.'],
  ['equipment', 'Equipment and identifiers (optional)', false, 'Models, asset IDs, serial numbers if appropriate. Avoid credentials.'],
  ['recommendations', 'Recommendations (optional)', false, 'Follow-up work to quote separately; not automatically authorized.'],
];

/** Admin and assigned contractors use the same server-verified workflow. */
export default function ServiceWorkPanel({ job }) {
  const { currentUser, userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin';
  const canOperate = Boolean(currentUser && (isAdmin || job.contractorId === currentUser.uid));
  const [report, setReport] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [equipmentInstalled, setEquipmentInstalled] = useState(false);
  const [assetTagNotPresent, setAssetTagNotPresent] = useState(false);
  const [assetTagNote, setAssetTagNote] = useState('');
  const [form, setForm] = useState(blankReport);
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!canOperate || !job?.id) return;
    return onSnapshot(doc(db, 'serviceReports', job.id), snap => {
      if (snap.exists()) {
        const data = snap.data();
        setReport(data);
        if (data.status === 'changes_requested') {
          setForm(Object.fromEntries(Object.keys(blankReport).map(key => [key, data[key] || ''])));
          setEquipmentInstalled(data.equipmentInstalled === true);
          setAssetTagNotPresent(data.assetTagNotPresent === true);
          setAssetTagNote(data.assetTagNote || '');
        }
      } else {
        setReport(null);
      }
    }, err => setError(`Unable to load report: ${err.message}`));
  }, [job?.id, canOperate]);

  useEffect(() => {
    if (!canOperate || !job?.id) return;
    return onSnapshot(doc(db, 'serviceEvidence', job.id), snap => {
      setEvidence(snap.exists() && Array.isArray(snap.data()?.photos) ? snap.data().photos : []);
    }, err => setError(`Unable to load work photos: ${err.message}`));
  }, [job?.id, canOperate]);

  async function run(action, payload = {}) {
    setBusy(true);
    setError(''); setNotice('');
    try {
      await httpsCallable(getFunctions(), action)({ jobId: job.id, ...payload });
      setNotice(action === 'startServiceWork' ? 'Work started.' :
        action === 'submitServiceReport' ? 'Report submitted for admin review.' :
        action === 'requestServiceReportChanges' ? 'Report returned for corrections.' : 'Report approved and delivered to the client.');
      if (action === 'submitServiceReport') setForm(blankReport);
      if (action === 'requestServiceReportChanges') setFeedback('');
    } catch (err) { setError(err.message || 'Action failed.'); }
    finally { setBusy(false); }
  }

  if (!canOperate) return <p className="text-sm text-gray-600">Only the assigned technician or Chuko Logic admin can manage service execution.</p>;
  const state = job.status;
  const isWaiting = state === 'pending_review';
  const has = category => evidence.some(p => p.category === category);
  const hasAfter = has('after');
  const hasBefore = has('before');
  const photosRequired = job.evidencePolicyVersion === 1;
  const equipmentEvidenceReady = !equipmentInstalled || (has('equipment') && has('serial') && (has('asset_tag') || (assetTagNotPresent && assetTagNote.trim().length >= 5)));

  return (
    <div className="space-y-5">
      <div className="rounded-lg bg-slate-50 border p-4 text-sm text-slate-700">
        <p><strong>Work order:</strong> {job.serviceType || job.id}</p>
        <p><strong>Status:</strong> {String(state).replace(/[_-]/g, ' ')}</p>
        {job.schedule?.date && <p><strong>Appointment:</strong> {job.schedule.date}, {job.schedule.startTime}–{job.schedule.endTime} CT</p>}
        <p className="mt-2">Keep the approved quote scope unchanged. Additional work needs a separately authorized change.</p>
      </div>
      {error && <p role="alert" className="p-3 bg-red-50 border border-red-200 rounded text-red-800">{error}</p>}
      {notice && <p role="status" className="p-3 bg-green-50 border border-green-200 rounded text-green-800">{notice}</p>}

      {state === 'scheduled' && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold">Before-work documentation</h3>
          <p className="text-sm text-gray-700">Photograph the condition of the work area before beginning. A before photo is required before check-in.</p>
          <ServiceEvidenceUploader job={job} category="before" disabled={busy} />
          <ServiceEvidenceGallery photos={evidence.filter(p => p.category === 'before')} />
          <button disabled={busy || !job.depositPaid || !hasBefore} type="button" onClick={() => run('startServiceWork')}
            className="bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50">{busy ? 'Working…' : 'Start Work / Check In'}</button>
          {!hasBefore && <p className="text-sm text-gray-700">Upload and register at least one before photo to enable check-in.</p>}
        </div>
      )}

      {state === 'in-progress' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Service report</h3>
          {report?.status === 'changes_requested' && (
            <div className="rounded bg-amber-50 border border-amber-200 p-3 text-amber-900 text-sm">
              <strong>Corrections requested:</strong> {report.reviewFeedback}
            </div>
          )}
          {fields.map(([key, label, required, placeholder]) => (
            <label key={key} className="block space-y-1 text-sm font-medium text-gray-800">
              <span>{label}{required ? ' *' : ''}</span>
              <textarea value={form[key]} onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                rows={key === 'workPerformed' ? 5 : 3} placeholder={placeholder} required={required}
                className="block w-full border border-gray-300 rounded-lg p-3 font-normal" />
            </label>
          ))}
          <section className="border-t pt-5 space-y-4">
            <h4 className="text-lg font-semibold">Work photos</h4>
            <p className="text-sm text-gray-700">Provide at least one after photo. If you installed equipment, also photograph the device, its serial number and its asset tag (if present).</p>
            <ServiceEvidenceUploader job={job} category="after" disabled={busy} />
            <label className="flex gap-3 items-center font-medium"><input type="checkbox" checked={equipmentInstalled} onChange={e => setEquipmentInstalled(e.target.checked)} /> Equipment was installed or replaced</label>
            {equipmentInstalled && <div className="space-y-3 pl-3 border-l-2 border-gray-200">
              <ServiceEvidenceUploader job={job} category="equipment" disabled={busy} />
              <ServiceEvidenceUploader job={job} category="serial" disabled={busy} />
              <ServiceEvidenceUploader job={job} category="asset_tag" disabled={busy || assetTagNotPresent} />
              <label className="flex gap-2 items-center text-sm"><input type="checkbox" checked={assetTagNotPresent} onChange={e => setAssetTagNotPresent(e.target.checked)} /> No asset tag is present or applicable</label>
              {assetTagNotPresent && <label className="block text-sm font-semibold">Explanation
                <textarea rows={2} value={assetTagNote} onChange={e => setAssetTagNote(e.target.value)} className="block w-full mt-1 rounded border p-2" placeholder="Explain why no asset tag is available" />
              </label>}
            </div>}
            <ServiceEvidenceGallery photos={evidence} />
            <p className="text-xs text-gray-700">Do not photograph passwords, private customer data or payment-card details. JPG/PNG/WEBP, up to 6 MB each.</p>
          </section>
          <button type="button" disabled={busy || form.diagnosis.trim().length < 10 || form.workPerformed.trim().length < 10 || form.testsPerformed.trim().length < 5 || (photosRequired && (!hasBefore || !hasAfter || !equipmentEvidenceReady))}
            onClick={() => run('submitServiceReport', { report: { ...form, equipmentInstalled, assetTagNotPresent, assetTagNote } })}
            className="bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50">{busy ? 'Submitting…' : 'Submit for Admin Review'}</button>
        </div>
      )}

      {isWaiting && <p className="rounded bg-amber-50 border border-amber-200 p-3 text-amber-900">Report submitted. Awaiting Chuko Logic quality review.</p>}
      {(isWaiting || (isAdmin && report?.status === 'approved')) && report && (
        <section className="space-y-3 border rounded-lg p-4">
          <h3 className="text-lg font-semibold">{report.status === 'approved' ? 'Approved report' : 'Submitted report'} — v{report.version}</h3>
          {fields.map(([key, label]) => <div key={key} className="text-sm"><strong>{label.replace(' (optional)', '')}</strong><p className="whitespace-pre-wrap text-gray-700">{report[key] || 'Not provided'}</p></div>)}
          <ServiceEvidenceGallery photos={report.photos || []} />
          <p className="text-xs text-gray-500">Submitted by {report.submittedBy}</p>
          {isAdmin && isWaiting && (
            <div className="border-t pt-4 space-y-3">
              <p className="text-sm text-gray-700">Approval publishes this report to the customer and marks service complete; it does not collect the final balance.</p>
              <button type="button" disabled={busy} onClick={() => run('approveServiceReport')}
                className="bg-green-700 text-white px-4 py-2 rounded-lg disabled:opacity-50">Approve & Complete Job</button>
              <label className="block text-sm font-semibold">Return for corrections
                <textarea className="block w-full border rounded p-2 mt-1 font-normal" rows={3} value={feedback}
                  onChange={e => setFeedback(e.target.value)} placeholder="Explain what needs to change (minimum 5 characters)" />
              </label>
              <button type="button" disabled={busy || feedback.trim().length < 5} onClick={() => run('requestServiceReportChanges', { feedback: feedback.trim() })}
                className="border border-amber-600 text-amber-900 px-4 py-2 rounded-lg disabled:opacity-50">Request Changes</button>
            </div>
          )}
        </section>
      )}
      {isAdmin && state === 'completed' && job.serviceReportStatus === 'approved' && <ServiceEvidenceAddendum job={job} />}
      {state === 'completed' && <p className="text-sm text-green-800">This job has been completed after admin review. The customer can see the approved report in Job Details.</p>}
      {!['scheduled', 'in-progress', 'pending_review', 'completed'].includes(state) &&
        <p className="text-sm text-gray-600">Execution becomes available after scheduling and deposit verification.</p>}
    </div>
  );
}
